import { create } from "zustand";
import {
  collection,
  query,
  orderBy,
  limit,
  onSnapshot,
  addDoc,
  arrayUnion,
  updateDoc,
  doc,
  deleteField,
} from "@firebase/firestore";
import { db } from "../lib/firebase";
import { format } from "date-fns";

const useChatStore = create((set) => ({
  userChats: [],
  replyToMsg: null,
  setReplyToMsg: async (message) => {
    set({ replyToMsg: message });
  },
  setUserChats: async () => {
    try {
      const q = query(
        collection(db, "chats"),
        orderBy("createdAt", "desc"),
        limit(50)
      );

      const unsubscribe = onSnapshot(q, (snapshot) => {
        const chatsData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        const groupedChats = chatsData.reduce((acc, chat) => {
          const dateKey = format(
            new Date(chat.createdAt.seconds * 1000),
            "yyyy-MM-dd"
          );
          if (!acc[dateKey]) {
            acc[dateKey] = [];
          }
          acc[dateKey].push(chat);
          return acc;
        }, {});

        // Convert groupedChats object into an array of arrays sorted by date
        const sortedChats = Object.entries(groupedChats)
          .sort(([dateA], [dateB]) => dateA.localeCompare(dateB)) // Sort in descending order by date
          .map(([date, chats]) => ({
            date,
            chats: chats.reverse(),
          }));

        set({ userChats: sortedChats });
      });

      return unsubscribe;
    } catch (error) {
      console.error("Error getting documents: ", error);
    }
  },
  postUserChat: async (msgPayload, scrollTo) => {
    try {
      const newMessage = await addDoc(collection(db, "chats"), {
        text: msgPayload.message.trim(),
        uid: msgPayload.uid,
        createdAt: new Date(),
        ...(msgPayload.replyTo && { replyTo: msgPayload.replyTo }),
      });

      scrollTo.current.scrollIntoView();

      return newMessage;
    } catch (error) {
      console.log(error);
    }
  },
  postUploadedPic: async (uid, fileMetaData, scrollTo) => {
    try {
      const newMessage = await addDoc(collection(db, "chats"), {
        text: "",
        uid: uid,
        imageName: fileMetaData.metadata.name,
        imageMetaData: {
          fullPath: fileMetaData.metadata.fullPath,
          size: fileMetaData.metadata.size,
          contentType: fileMetaData.metadata.contentType,
        },
        createdAt: new Date(),
      });

      scrollTo.current.scrollIntoView();

      return newMessage;
    } catch (error) {
      console.log(error);
    }
  },
  postUploadedVideo: async (uid, fileMetaData, scrollTo) => {
    try {
      const newMessage = await addDoc(collection(db, "chats"), {
        text: "",
        uid: uid,
        videoName: fileMetaData.metadata.name,
        videoMetaData: {
          fullPath: fileMetaData.metadata.fullPath,
          size: fileMetaData.metadata.size,
          contentType: fileMetaData.metadata.contentType,
        },
        createdAt: new Date(),
      });

      scrollTo.current.scrollIntoView();

      return newMessage;
    } catch (error) {
      console.log(error);
    }
  },
  setQuickReaction: async (uid, msg) => {
    try {
      const chatDocRef = doc(db, "chats", msg.id);
      let reactionData = {};

      if (msg.reactions) {
        reactionData = {
          reactions: deleteField(),
        };
      } else {
        reactionData = {
          "reactions.quick_reaction": arrayUnion(uid),
        };
      }

      await updateDoc(chatDocRef, reactionData);
    } catch (error) {
      console.log(error);
    }
  },
}));

export default useChatStore;
